import { useState, useEffect, MouseEvent } from 'react';
import { AddedToCart } from './AddedToCart.js';
import { giveToSchool } from './giveToSchool';
import { Svg } from './Svg';
import { PortaledLightBoxWrapper } from '../common/PortaledLightBoxWrapper';

interface MonthlyToggleProps {
    monthly: boolean,
    setMonthly: (monthly: boolean) => void,
    uuid: string,
}
const MonthlyToggle = ({ monthly, setMonthly, uuid }: MonthlyToggleProps) => {
    const name = `monthly-${uuid}`;
    const trueId = `monthly-true-${uuid}`;
    const falseId = `monthly-false-${uuid}`;

    return (
        <fieldset className="donation-type">
            <input id={trueId} type="radio" name={name} value="true" defaultChecked={monthly} onChange={(e) => { setMonthly(true); }} />
            <label htmlFor={trueId}><Svg icon="check" attributes={{ role: 'presentation' }} additionalClasses="presentation" /> Monthly</label>
            <input id={falseId} type="radio" name={name} value="false" defaultChecked={!monthly} onChange={(e) => { setMonthly(false); }} />
            <label htmlFor={falseId}><Svg icon="check" attributes={{ role: 'presentation' }} additionalClasses="presentation" /> One-time</label>
        </fieldset>
    );
};

interface AmountButtonProps {
    amount: number,
    completing: boolean,
    handleGiveAmount: (e: MouseEvent, amount: number) => void,
}
const AmountButton = ({ amount, completing, handleGiveAmount }: AmountButtonProps) => {
    return (
        <button
            className={`button-primary suggested-amount ${completing ? 'complete' : ''}`}
            onClick={(e) => { handleGiveAmount(e, amount); }}
            tabIndex={0}
        >${Math.round(amount)}</button>
    );
};

const REGULAR_AMOUNTS = [50, 100, 250];
// Separate because this is normally "cost to complete". (But note also that the cost-to-complete on a *school* give
// widget is Fake?)
const DEFAULT_LAST_GIVE_BUTTON_AMOUNT = 500;

const MONTHLY_AMOUNTS = [5, 10, 25, 50, 100];
const MONTHLY_LAST_GIVE_BUTTON_AMOUNT = 250;

interface Amounts {
    regular: number[],
    final: number,
}

interface AnchoredAmountsProps {
    handleGiveAmount: (e: MouseEvent, amount: number) => void,
    totalCostToComplete?: number,
    monthly: boolean,
}

const getAmounts = (monthly: boolean, totalCostToComplete?: number): Amounts => {
    if (monthly) {
        return {
            regular: MONTHLY_AMOUNTS,
            final: MONTHLY_LAST_GIVE_BUTTON_AMOUNT,
        };
    } else {
        return {
            regular: REGULAR_AMOUNTS,
            final: totalCostToComplete && totalCostToComplete > DEFAULT_LAST_GIVE_BUTTON_AMOUNT ? totalCostToComplete : DEFAULT_LAST_GIVE_BUTTON_AMOUNT,
        };
    }
};

const AnchoredAmounts = ({ handleGiveAmount, totalCostToComplete, monthly }: AnchoredAmountsProps) => {
    const amounts = getAmounts(monthly, totalCostToComplete);

    const regularButtons = amounts.regular.map((amount, i) => {
        return <AmountButton key={i} amount={amount} completing={false} handleGiveAmount={handleGiveAmount} />;
    });
    const finalButtonCompleting = !monthly && !!totalCostToComplete && totalCostToComplete > DEFAULT_LAST_GIVE_BUTTON_AMOUNT;
    const finalButton = (
        <AmountButton
            key={amounts.regular.length}
            amount={amounts.final}
            completing={finalButtonCompleting}
            handleGiveAmount={handleGiveAmount}
        />
    );

    const allButtons = [...regularButtons, finalButton];
    return (
        <div className='anchored-amounts'>
            {monthly ? <div className='monthly-label'></div> : null}
            {allButtons}
        </div>
    );
};

interface SchoolGiveWidgetProps {
    // A number or numeric string, depending on where you got it from. It would be cleaner if this were a number
    // everywhere, but that's a lot of work and it changes basically nothing here (all we do is pass it into inputs etc.)
    schoolId: number | string,
    schoolName: string,
    // A page-specific implementation detail that leaked out into the component API. I'm not sure what it means.
    useAjax?: boolean,
    // Not sure how this is supposed to work. We can't calculate need on a school-wide basis.
    totalCostToComplete?: number,
    onSupportAnotherClassroom?: () => void,
    monthlySchoolDonationEnabled: boolean,
    // If provided and true, once you click the give button we'll open the amount-buttons and monthly-toggle in a
    // lightbox.
    useLightbox?: boolean,
}

/**
 * A give widget (that is, a form with an amount-input and a 'give' submit button) that will, on submit, add a
 * 'school-level giving' donation to the specified school to your cart.
 */
export const SchoolGiveWidget = ({ schoolId, schoolName, useAjax, totalCostToComplete, onSupportAnotherClassroom, monthlySchoolDonationEnabled, useLightbox }: SchoolGiveWidgetProps) => {
    const [amount, setAmount] = useState(0);
    const [requestComplete, setRequestComplete] = useState(false);
    const [isGiveButtonToggled, setIsGiveButtonToggled] = useState(false);
    const [isCustomAmountOpen, setIsCustomGiveAmountOpen] = useState(false);
    const [customAmount, setCustomGiveAmount] = useState('');
    const [monthly, setMonthly] = useState(false);
    // We need to generate IDs for input-labels. They need to be globally unique, and they need to *stay* globally
    // unique even if we mount more than one instance of this component in the page. So we generate a UUID and embed
    // that in these IDs.
    const [idUuid] = useState(crypto.randomUUID());

    const handleGiveAmount = (e: MouseEvent, newAmount: number) => {
        setAmount(newAmount);
        if (useAjax) {
            e.preventDefault();
            giveToSchool(schoolId, newAmount, monthly, onSuccess, onFailure);
        }
    };

    const handleCustomAmount = (e: MouseEvent, newAmount: string) => {
        if (!customAmount) {
            return;
        }
        handleGiveAmount(e, parseInt(newAmount, 10));
    };

    const onSuccess = () => {
        setRequestComplete(true);
        setCustomGiveAmount('');
        setIsCustomGiveAmountOpen(false);
        setIsGiveButtonToggled(pageName === 'school_info');
    };

    // The full expected type is whatever jQuery passes into an onFailure handler.
    interface PartialErrorData {
        responseText: string;
    }
    const hasResponseText = (data: unknown): data is PartialErrorData => {
        return typeof data === 'object' && data !== null && 'responseText' in data && typeof data.responseText === 'string';
    };
    const onFailure = (data: unknown) => {
        if (hasResponseText(data) && data.responseText === 'amount') {
            alert('Please enter at least $1.');
        } else {
            alert('Something went wrong!');
        }
    };

    // On mount, munge last-search cookies as if the user had just searched for this school.
    // We use that last-search cookie to update the user's favorites when they make their donation.
    useEffect(() => {
        // School-search-param is 'school', not 'schoolid'
        const schoolSearchUrl = `${baseURL}donors/search.html?school=${schoolId}`;
        window.createCookie('searchTermsURL', schoolSearchUrl, 1);
        window.createCookie('searchTermsDesc', window.b64EncodeUnicode(schoolName), 1);
    }, [schoolId, schoolName]);

    const getGiveSection = function () {
        const toggle = monthlySchoolDonationEnabled ? <MonthlyToggle monthly={monthly} setMonthly={setMonthly} uuid={idUuid} /> : null;

        let giveSection;
        if (!isGiveButtonToggled) {
            giveSection =
                <button className='button-primary' onClick={() => setIsGiveButtonToggled(!isGiveButtonToggled)} tabIndex={0}>
                    Give to this school
                </button>;
        } else if (!isCustomAmountOpen) {
            giveSection =
                <div className='suggested-give-amounts'>
                    {toggle}
                    <AnchoredAmounts handleGiveAmount={handleGiveAmount} totalCostToComplete={totalCostToComplete} monthly={monthly} />
                    <div className='custom'>
                        <button className='text-link custom-amount' onClick={() => setIsCustomGiveAmountOpen(!isCustomAmountOpen)} tabIndex={0}>Give another amount</button>
                    </div>
                </div>
            ;
        } else {
            giveSection =
               <div className='custom'>
                   {toggle}
                   <div className="custom-give-input-container">
                       <label className="custom-give-input">
                           <input name='value' type='number' autoFocus inputMode="decimal" pattern="[0-9]*\.?[0-9]{0,2}" min="0" step="0.01" className='' placeholder='100' value={customAmount} onChange={(e) => setCustomGiveAmount(e.target.value)} />
                       </label>
                       <input name='schoolId' type='hidden' value={schoolId} />
                       <div className='inline-button'><button onClick={(e) => handleCustomAmount(e, customAmount)} className='button-primary'>Give {monthly ? 'monthly' : ''}</button></div>
                   </div>
               </div>;
        }

        // Hack: once we're satisfied with how this looks, we can decouple it from the monthly-donation flag
        if (useLightbox && isGiveButtonToggled && monthlySchoolDonationEnabled) {
            giveSection = (
                <PortaledLightBoxWrapper additionalClasses="fixed give-widget form-redux" targetElementId={'mainBody'} unMountHandler={() => { setIsGiveButtonToggled(false); }}>
                    {giveSection}
                </PortaledLightBoxWrapper>
            );
        }

        return (
            <div className='give-container'>
                {giveSection}
                <div className="tooltip-wrapper disclaimer full-width subtle-link" aria-label="tooltip">
                    How is my gift used?
                    <span className="hover-tooltip hover-above">
                            Your donation will be applied to a classroom project, if available. If no projects are listed at this school, we&rsquo;ll send the funds to a teacher for later use.
                        <a className="text-link" href="https://help.donorschoose.org/hc/en-us/articles/22411454874775">Learn more</a>
                    </span>
                </div>
            </div>
        );
    };

    if (requestComplete) {
        const combinedOnCloseHandler = () => {
            setRequestComplete(false);
            if (onSupportAnotherClassroom) {
                onSupportAnotherClassroom();
            }
        };

        return <AddedToCart
            amount={amount} //
            onClose={combinedOnCloseHandler}
        />;
    }

    if (useAjax) {
        return (
            <form className='form-redux'>
                {getGiveSection()}
            </form>
        );
    }

    return (
        <form className="school-give-section form-redux" method={'post'} action={`${secureBaseURL}donors/cart/school-donation`}>
            <input name="schoolId" type="hidden" value={schoolId} />
            <input name="value" type="hidden" value={amount} />
            {getGiveSection()}
        </form>
    );
};
